import { AmountType, CustomFeeAmountType } from '@common/entities';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { resetStoreAction } from 'state/commonActions';

import { AllChargeDetail, TaxAmount } from 'types/shopping-cart';

export enum CartChargeType {
  AddOn = 'addon',
  Rent = 'rent',
  Utility = 'utility',
  RentDeposit = 'rentdeposit',
  PetDeposit = 'petdeposit',
  ConditionalScreening = 'conditionalscreening',
}

export interface CoreChargeDate {
  date?: string;
  timezone_name?: string;
}

export type CartCharge = {
  id: number | string;
  type: CartChargeType;
  label: string;
  arTriggerId?: number;
  priceNumber: number;
  priceFormatted?: string;
  currencyCode?: string;
  dueNow: boolean;
  dueAtMoveIn: boolean;
  isRecurring: boolean;
  recurringFrequency?: string;
  isRange?: boolean;
  minPriceNumber?: number;
  maxPriceNumber?: number;
  minPriceFormatted?: string;
  maxPriceFormatted?: string;
  amountType?: AmountType;
  customFeeAmountType?: CustomFeeAmountType;
  amountDetail?: string;
  allChargesDetails?: AllChargeDetail[];
  arCodeName?: string;
  taxAmount?: TaxAmount;
  installmentId?: number | null;
  chargeStartDate?: string | CoreChargeDate | null;
  chargeEndDate?: string | CoreChargeDate | null;
  totalValueWithTax?: number;
  dueNowAmount?: number;
  installmentNumber?: number;
};

export type ShoppingCartReducer = {
  charges: CartCharge[];
  scheduledChargesLoaded: boolean;
  isSyncingCharges: boolean;
  scheduledChargesSyncError: boolean;
  scheduledChargesRetryCount: number;
  scheduledChargesRetryLimitReached: boolean;
};

export const SCHEDULED_CHARGES_MAX_RETRY_COUNT = 3;

const initialState: ShoppingCartReducer = {
  charges: [],
  scheduledChargesLoaded: false,
  isSyncingCharges: false,
  scheduledChargesSyncError: false,
  scheduledChargesRetryCount: 0,
  scheduledChargesRetryLimitReached: false,
};

export const shoppingCartSlice = createSlice({
  name: 'shoppingCart',
  initialState,
  reducers: {
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    setCartCharges: (state, action: PayloadAction<CartCharge[]>) => {
      return { ...state, charges: action.payload };
      // For now, disabling manually updating the shopping cart. It will allways be
      // loaded from the billing api response.
      // return { ...state, charges: action.payload };
    },
    resetCartCharges: (state, action: PayloadAction<CartCharge[]>) => {
      return { ...state, charges: action.payload };
    },
    setIsSyncingCharges: (state, action: PayloadAction<boolean>) => {
      return { ...state, isSyncingCharges: action.payload };
    },
    setScheduledChargesSyncStart: (state) => {
      return { ...state, isSyncingCharges: true, scheduledChargesLoaded: false };
    },
    setScheduledChargesSyncSuccess: (state) => {
      return {
        ...state,
        isSyncingCharges: false,
        scheduledChargesLoaded: true,
        scheduledChargesSyncError: false,
        scheduledChargesRetryCount: 0,
        scheduledChargesRetryLimitReached: false,
      };
    },
    setScheduledChargesNotApplicable: (state) => {
      return {
        ...state,
        charges: [],
        isSyncingCharges: false,
        scheduledChargesLoaded: true,
        scheduledChargesSyncError: false,
        scheduledChargesRetryCount: 0,
        scheduledChargesRetryLimitReached: false,
      };
    },
    setScheduledChargesSyncFailure: (state, action: PayloadAction<boolean>) => {
      const nextRetryCount = action.payload
        ? Math.min(state.scheduledChargesRetryCount + 1, SCHEDULED_CHARGES_MAX_RETRY_COUNT)
        : state.scheduledChargesRetryCount;

      return {
        ...state,
        isSyncingCharges: false,
        scheduledChargesLoaded: false,
        scheduledChargesSyncError: true,
        scheduledChargesRetryCount: nextRetryCount,
        scheduledChargesRetryLimitReached: nextRetryCount >= SCHEDULED_CHARGES_MAX_RETRY_COUNT,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetStoreAction, () => initialState);
  },
});

export const {
  setCartCharges,
  resetCartCharges,
  setIsSyncingCharges,
  setScheduledChargesSyncStart,
  setScheduledChargesSyncSuccess,
  setScheduledChargesNotApplicable,
  setScheduledChargesSyncFailure,
} = shoppingCartSlice.actions;
export default shoppingCartSlice.reducer;
